// ===========================================
// ANIMATION RUNNER (Declarative Sound System)
// ===========================================
// Animations declare their sounds, runner enforces playback.
// Sounds are PART of the animation definition, not afterthoughts.
// Single source of truth for all game sounds.
// ===========================================
//
// MIGRATION STATUS:
// -----------------
// playSound() is KEPT - used by both Pixi and DOM animations.
//
// MARKED FOR DELETION after React table pages are removed:
//   - animateRailToKittyV2, animateStagedToKittyV2 (only used by ControlPanel.tsx)
//   - runChipFlight (only used by legacy animations)
//   - railToKittyChipConfig (only used by legacy animations)
// ===========================================

import { getSfxVolumeNormalized, getOutcomeVolumeNormalized } from '../stores/tableSettingsStore';
import { isAudioSuppressed } from '../components/game/pixi/audio/audioSuppression';

// ===========================================
// SOUND IMPORTS
// ===========================================

// Chip sounds (7 variations)
import chips1 from '../assets/sfx/chips/chips1.wav';
import chips2 from '../assets/sfx/chips/chips2.wav';
import chips3 from '../assets/sfx/chips/chips3.wav';
import chips4 from '../assets/sfx/chips/chips4.wav';
import chips5 from '../assets/sfx/chips/chips5.wav';
import chips6 from '../assets/sfx/chips/chips6.wav';
import chips7 from '../assets/sfx/chips/chips7.wav';

// Deal sounds (2 variations)
import deal1 from '../assets/sfx/cards/deal/deal1.wav';
import deal2 from '../assets/sfx/cards/deal/deal2.wav';

// Rake sounds (2 variations)
import rake1 from '../assets/sfx/cards/rake/rake1.wav';
import rake2 from '../assets/sfx/cards/rake/rake2.wav';

// Outcome sounds (hand labels)
import winnerSfx from '../assets/sfx/HANDLABEL/WINNER.wav';
import loserSfx from '../assets/sfx/HANDLABEL/LOSER.wav';
import blackjackSfx from '../assets/sfx/HANDLABEL/BLACKJACK.wav';
import bustedSfx from '../assets/sfx/HANDLABEL/BUSTED.wav';
import pushSfx from '../assets/sfx/HANDLABEL/PUSH.wav';

// Split slide sound
import slideSfx from '../assets/sfx/split/slide.wav';

// ===========================================
// AUDIO POOLS (pre-created for fast playback)
// ===========================================

const chipAudioPool = [chips1, chips2, chips3, chips4, chips5, chips6, chips7].map(src => {
  const audio = new Audio(src);
  audio.preload = 'auto';
  return audio;
});

const dealAudioPool = [deal1, deal2].map(src => {
  const audio = new Audio(src);
  audio.preload = 'auto';
  return audio;
});

const rakeAudioPool = [rake1, rake2].map(src => {
  const audio = new Audio(src);
  audio.preload = 'auto';
  return audio;
});

// Single audio elements for one-shot sounds
const winnerAudio = new Audio(winnerSfx); winnerAudio.preload = 'auto';
const loserAudio = new Audio(loserSfx); loserAudio.preload = 'auto';
const blackjackAudio = new Audio(blackjackSfx); blackjackAudio.preload = 'auto';
const bustedAudio = new Audio(bustedSfx); bustedAudio.preload = 'auto';
const pushAudio = new Audio(pushSfx); pushAudio.preload = 'auto';
const slideAudio = new Audio(slideSfx); slideAudio.preload = 'auto';

// ===========================================
// BASE VOLUMES (scaled by user settings)
// ===========================================

export const BASE_VOLUMES = {
  chip: 0.5,
  deal: 0.6,
  rake: 0.1,
  slide: 0.1,
  // Outcome volumes (separate user control)
  winner: 0.05,
  loser: 0.10,
  blackjack: 0.05,
  busted: 0.10,
  push: 0.10,
};

// ===========================================
// SOUND PLAY FUNCTIONS
// ===========================================
// All functions accept optional volumeMultiplier (0.0-1.0, default 1.0)
// Use lower values for opponent sounds

function playChipLandSound(volumeMultiplier = 1.0): void {
  const userVolume = getSfxVolumeNormalized();
  if (userVolume === 0) return;
  const volume = BASE_VOLUMES.chip * userVolume * volumeMultiplier;

  const index = Math.floor(Math.random() * chipAudioPool.length);
  const audio = chipAudioPool[index];
  audio.volume = volume;
  audio.currentTime = 0;
  audio.play().catch(() => {});
}

function playDealSound(volumeMultiplier = 1.0): void {
  const userVolume = getSfxVolumeNormalized();
  if (userVolume === 0) return;
  const volume = BASE_VOLUMES.deal * userVolume * volumeMultiplier;

  const index = Math.floor(Math.random() * dealAudioPool.length);
  const audio = dealAudioPool[index];
  audio.volume = volume;
  audio.currentTime = 0;
  audio.play().catch(() => {});
}

function playRakeSound(volumeMultiplier = 1.0): void {
  const userVolume = getSfxVolumeNormalized();
  if (userVolume === 0) return;
  const volume = BASE_VOLUMES.rake * userVolume * volumeMultiplier;

  const index = Math.floor(Math.random() * rakeAudioPool.length);
  const audio = rakeAudioPool[index];
  audio.volume = volume;
  audio.currentTime = 0;
  audio.play().catch(() => {});
}

function playSlideSound(volumeMultiplier = 1.0): void {
  const userVolume = getSfxVolumeNormalized();
  if (userVolume === 0) return;
  slideAudio.volume = BASE_VOLUMES.slide * userVolume * volumeMultiplier;
  slideAudio.currentTime = 0;
  slideAudio.play().catch(() => {});
}

// Outcome sounds use separate volume control
function playOutcomeSound(audio: HTMLAudioElement, baseVolume: number, volumeMultiplier = 1.0): void {
  const userVolume = getOutcomeVolumeNormalized();
  if (userVolume === 0) return;
  audio.volume = baseVolume * userVolume * volumeMultiplier;
  audio.currentTime = 0;
  audio.play().catch(() => {});
}

// ===========================================
// SOUND REGISTRY
// ===========================================
// Maps sound names to play functions that accept volumeMultiplier

const soundRegistry: Record<string, (volumeMultiplier?: number) => void> = {
  // Direct play - animations handle timing/stagger, no queue needed
  chipLand: playChipLandSound,
  deal: playDealSound,
  rake: playRakeSound,
  slide: playSlideSound,

  // Outcome sounds (low frequency, separate volume control)
  outcomeWinner: (v = 1.0) => playOutcomeSound(winnerAudio, BASE_VOLUMES.winner, v),
  outcomeLoser: (v = 1.0) => playOutcomeSound(loserAudio, BASE_VOLUMES.loser, v),
  outcomeBlackjack: (v = 1.0) => playOutcomeSound(blackjackAudio, BASE_VOLUMES.blackjack, v),
  outcomeBusted: (v = 1.0) => playOutcomeSound(bustedAudio, BASE_VOLUMES.busted, v),
  outcomePush: (v = 1.0) => playOutcomeSound(pushAudio, BASE_VOLUMES.push, v),
};

// ===========================================
// PUBLIC API - Play sounds
// ===========================================

export interface PlaySoundOptions {
  /** Volume multiplier 0.0-1.0 (default 1.0). Use lower values for opponent sounds. */
  volume?: number;
}

/**
 * Play a sound by name with optional volume adjustment.
 * @param soundName - The sound to play (chipLand, deal, rake, outcomeWinner, etc.)
 * @param options - Optional settings like volume multiplier
 *
 * @example
 * playSound('chipLand')                    // Full volume (player)
 * playSound('chipLand', { volume: 0.4 })   // 40% volume (opponent)
 * playSound('outcomeWinner', { volume: 0.5 })
 */
export function playSound(soundName: string, options?: PlaySoundOptions): void {
  // Drop sounds while suppressed (tab hidden or settle window) — prevents SFX burst on tab return
  if (isAudioSuppressed()) return;

  const volumeMultiplier = options?.volume ?? 1.0;
  soundRegistry[soundName]?.(volumeMultiplier);
}

// ===========================================
// TYPES
// ===========================================

export type SoundTrigger =
  | { at: 'finish'; sound: string }  // Use onfinish (frame-perfect)
  | { at: number; sound: string };   // Use setTimeout (0.0-1.0)

export interface ChipFlightConfig {
  duration: number;
  easing: string;
  sounds: SoundTrigger[];
}

// ===========================================
// ANIMATION CONFIGS
// ===========================================
// Declare animations with their sounds

export const railToKittyChipConfig: ChipFlightConfig = {
  duration: 250,
  easing: 'ease-out',
  sounds: [
    { at: 'finish', sound: 'chipLand' },
  ],
};

// ===========================================
// ANIMATION RUNNER
// ===========================================

/**
 * Run a single chip flight animation with declarative sounds.
 * Sounds are scheduled based on config, not manually called.
 */
export function runChipFlight(
  chip: HTMLElement,
  translateX: number,
  translateY: number,
  endScale: number,
  config: ChipFlightConfig
): Promise<void> {
  return new Promise((resolve) => {
    const { duration, easing, sounds } = config;
    const soundTimeouts: ReturnType<typeof setTimeout>[] = [];

    // Schedule sounds based on config
    sounds.forEach(({ at, sound }) => {
      const playSoundIfNotSuppressed = () => {
        if (!isAudioSuppressed()) soundRegistry[sound]?.();
      };

      if (at === 'finish') {
        // Will be triggered in onfinish - mark for later
        // (handled below)
      } else {
        // Schedule via setTimeout
        const delay = duration * at;
        soundTimeouts.push(setTimeout(playSoundIfNotSuppressed, delay));
      }
    });

    // Get sounds that should play on finish
    const finishSounds = sounds.filter(s => s.at === 'finish');

    // Run WAAPI animation
    const animation = chip.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: `translate(${translateX}px, ${translateY}px) scale(${endScale})`, opacity: 1 },
      ],
      { duration, easing, fill: 'forwards' }
    );

    animation.onfinish = () => {
      // Play finish sounds (frame-perfect) — check suppression at play time
      finishSounds.forEach(({ sound }) => {
        if (!isAudioSuppressed()) soundRegistry[sound]?.();
      });

      // Cleanup chip
      chip.style.opacity = '0';
      chip.style.pointerEvents = 'none';
      resolve();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          chip.remove();
        });
      });
    };

    animation.oncancel = () => {
      // Clear scheduled sounds - animation didn't complete
      soundTimeouts.forEach(clearTimeout);

      chip.style.opacity = '0';
      chip.style.pointerEvents = 'none';
      resolve();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          chip.remove();
        });
      });
    };
  });
}

// ===========================================
// HIGH-LEVEL ANIMATIONS (V2 - Declarative)
// ===========================================

import { CHIP_IMAGES } from '../assets/themes/table2/chips';
import { CHIP_SIZES, breakdownChips } from './chipAnimations';

export interface RailToKittyV2Config {
  chips: number[];
  handIndex?: number;
  staggerDelay?: number;
  onChipEnd?: (value: number, index: number) => void;
}

/**
 * V2: Animate chips from rail to kitty with declarative sounds.
 * Sound is guaranteed by the animation config, not manual calls.
 */
export async function animateRailToKittyV2(config: RailToKittyV2Config): Promise<void> {
  const { chips, handIndex = 0, staggerDelay = 80, onChipEnd } = config;

  console.log('[animateRailToKittyV2] Starting with chips:', chips, 'handIndex:', handIndex);

  // Get player's kitty element
  const kittyElement = document.querySelector('[data-player-kitty="current-user"]');
  if (!kittyElement) {
    console.warn('[animateRailToKittyV2] Player kitty not found');
    return;
  }

  if (chips.length === 0) {
    console.warn('[animateRailToKittyV2] No chips to animate');
    return;
  }

  const sortedChips = [...chips].sort((a, b) => a - b);
  const uniqueDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);

  // Capture source positions from chip rail
  const denomSourceRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const railChip = document.querySelector(`[data-chip-rail="${denom}"]`);
    if (railChip) {
      denomSourceRects.set(denom, railChip.getBoundingClientRect());
    }
  }

  if (denomSourceRects.size === 0) {
    console.warn('[animateRailToKittyV2] No rail chips found');
    return;
  }

  // Get kitty rect and target rects
  const kittyRect = kittyElement.getBoundingClientRect();
  const denomTargetRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const handSelector = `[data-hand-index="${handIndex}"]`;
    const denomChip = kittyElement.querySelector(`[data-chip-denom="${denom}"]${handSelector}[data-chip-index="0"]`);
    if (denomChip) {
      denomTargetRects.set(denom, denomChip.getBoundingClientRect());
    }
  }

  // Get animation layer
  const chipAnimLayer = document.getElementById('chip-animation-layer');

  // Animate each chip with stagger
  const animations: Promise<void>[] = [];

  sortedChips.forEach((chipValue, index) => {
    const sourceRect = denomSourceRects.get(chipValue);
    const targetRect = denomTargetRects.get(chipValue) || kittyRect;

    if (!sourceRect || sourceRect.width === 0 || targetRect.width === 0) {
      return;
    }

    const animPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        // Create chip element
        const chip = document.createElement('div');
        const chipImg = CHIP_IMAGES[chipValue as keyof typeof CHIP_IMAGES];
        const sourceSize = CHIP_SIZES.rail;

        const startX = sourceRect.left + sourceRect.width / 2 - sourceSize / 2;
        const startY = sourceRect.top + sourceRect.height / 2 - sourceSize / 2;

        chip.style.cssText = `
          position: fixed;
          left: ${startX}px;
          top: ${startY}px;
          width: ${sourceSize}px;
          height: ${sourceSize}px;
          background-image: url(${chipImg});
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
          border-radius: 50%;
          pointer-events: none;
          z-index: 10000;
          will-change: transform, opacity;
        `;

        if (chipAnimLayer) {
          chipAnimLayer.appendChild(chip);
        } else {
          document.body.appendChild(chip);
        }

        // Calculate translation
        const targetCenterX = targetRect.left + targetRect.width / 2;
        const targetCenterY = targetRect.top + targetRect.height / 2;
        const startCenterX = startX + sourceSize / 2;
        const startCenterY = startY + sourceSize / 2;
        const translateX = targetCenterX - startCenterX;
        const translateY = targetCenterY - startCenterY;
        const endScale = (targetRect.width || CHIP_SIZES.kittyMedium) / sourceSize;

        // Run animation with declarative sounds
        runChipFlight(chip, translateX, translateY, endScale, railToKittyChipConfig)
          .then(() => {
            onChipEnd?.(chipValue, index);
            resolve();
          });
      }, index * staggerDelay);
    });

    animations.push(animPromise);
  });

  await Promise.all(animations);
  console.log('[animateRailToKittyV2] Complete');
}

// ===========================================
// STAGED TO KITTY (BET BUTTON) - V2
// ===========================================

export interface StagedToKittyV2Config {
  chips: number[];
  getStagedRect: (denom: number) => DOMRect | null;
  getKittyRect: (denom: number) => DOMRect | null;
  onChipEnd?: (value: number, index: number) => void;
  staggerDelay?: number;
}

/**
 * V2: Animate chips from staged bet area to kitty with declarative sounds.
 * This is the BET button flow - chips move from staged area to player's kitty.
 */
export async function animateStagedToKittyV2(config: StagedToKittyV2Config): Promise<void> {
  const { chips, getStagedRect, getKittyRect, onChipEnd, staggerDelay = 80 } = config;

  console.log('[animateStagedToKittyV2] Starting with chips:', chips);

  if (chips.length === 0) {
    console.warn('[animateStagedToKittyV2] No chips to animate');
    return;
  }

  // Get animation layer
  const chipAnimLayer = document.getElementById('chip-animation-layer');

  // Animate each chip with stagger
  const animations: Promise<void>[] = [];

  chips.forEach((chipValue, index) => {
    const sourceRect = getStagedRect(chipValue);
    const targetRect = getKittyRect(chipValue);

    if (!sourceRect || !targetRect) {
      console.log(`[animateStagedToKittyV2] Skipping chip ${chipValue}: no rects`);
      return;
    }

    const animPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        // Create chip element
        const chip = document.createElement('div');
        const chipImg = CHIP_IMAGES[chipValue as keyof typeof CHIP_IMAGES];
        const sourceSize = CHIP_SIZES.staged;

        const startX = sourceRect.left + sourceRect.width / 2 - sourceSize / 2;
        const startY = sourceRect.top + sourceRect.height / 2 - sourceSize / 2;

        chip.style.cssText = `
          position: fixed;
          left: ${startX}px;
          top: ${startY}px;
          width: ${sourceSize}px;
          height: ${sourceSize}px;
          background-image: url(${chipImg});
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
          border-radius: 50%;
          pointer-events: none;
          z-index: 10000;
          will-change: transform, opacity;
        `;

        if (chipAnimLayer) {
          chipAnimLayer.appendChild(chip);
        } else {
          document.body.appendChild(chip);
        }

        // Calculate translation
        const targetCenterX = targetRect.left + targetRect.width / 2;
        const targetCenterY = targetRect.top + targetRect.height / 2;
        const startCenterX = startX + sourceSize / 2;
        const startCenterY = startY + sourceSize / 2;
        const translateX = targetCenterX - startCenterX;
        const translateY = targetCenterY - startCenterY;
        const endScale = (targetRect.width || CHIP_SIZES.kittyMedium) / sourceSize;

        // Run animation with declarative sounds
        runChipFlight(chip, translateX, translateY, endScale, railToKittyChipConfig)
          .then(() => {
            onChipEnd?.(chipValue, index);
            resolve();
          });
      }, index * staggerDelay);
    });

    animations.push(animPromise);
  });

  await Promise.all(animations);
  console.log('[animateStagedToKittyV2] Complete');
}
